import { orderBy, uniqueId } from 'lodash';
import React, { useState } from 'react';

import RichText from 'components/RichText/RichText';
import Text from 'components/Text/Text';
import useOverElement from 'hooks/useOverElement';
import { useCopyStore } from 'store';
import { handleMouseEnter, handleMouseLeave } from 'utils/cursorGlobal';
import { OfficeCMS } from 'utils/sharedStrapiQueries/sharedTypes';

import * as S from '../AboutPage.styles';

export interface OfficeSectionProps {
  sectionTitle: string;
  offices: Array<{
    office: OfficeCMS;
  }>;
}

const OfficeSection: React.FunctionComponent<OfficeSectionProps> = ({
  offices,
  sectionTitle,
}) => {
  const { globalSettings } = useCopyStore(({ copy }) => copy);
  const officesArr = offices && offices.map(({ office }) => office);
  const orderedOfficesArr = orderBy(officesArr, ['office', 'heading'], ['asc']);

  const [office, setOffice] = useState(0);

  const [ref] = useOverElement();

  const hoverTextColor =
    globalSettings?.buttonsHoverColor ||
    globalSettings?.buttonsHoverDefaultColors;

  const sliderItemsComp = orderedOfficesArr.map(({ heading }, index, arr) => (
    <S.SliderItem key={uniqueId()} hoverTextColor={hoverTextColor}>
      <button onClick={() => setOffice(index)}>
        <RichText
          as="p"
          size="smallBody"
          richText={heading}
          defaultFormatting
        />
      </button>
      {arr.length - 1 !== index && <S.SliderDivider />}
    </S.SliderItem>
  ));

  const currentOffice = orderedOfficesArr[office];
  const officeCard = (
    <>
      <S.OfficeInfoWrapper>
        <S.OfficeHeading>
          <RichText
            as="h1"
            size="heading1"
            richText={currentOffice.textField}
            defaultFormatting
          />
        </S.OfficeHeading>
        <S.OfficeInfo>
          <RichText
            as="p"
            size="smallBody"
            richText={currentOffice.addressLabel}
            defaultFormatting
          />
        </S.OfficeInfo>
        <S.OfficeAddresses>
          <S.TextRich richText={currentOffice.addressLine1} defaultSpacing />
          {currentOffice.addressLine2 && (
            <>
              <br />
              <S.TextRich
                richText={currentOffice.addressLine2}
                defaultSpacing
              />
            </>
          )}
        </S.OfficeAddresses>
        <S.OfficeInfo>
          <RichText
            as="p"
            size="smallBody"
            richText={currentOffice.contactLabel}
            defaultFormatting
          />
        </S.OfficeInfo>
        <S.OfficeInfoBox hoverTextColor={hoverTextColor}>
          <Text
            as="a"
            size="smallBody"
            text={currentOffice.telephoneNumber}
            href={`tel:${currentOffice.telephoneNumber}`}
          />
          <div>
            {currentOffice?.emailFields?.map(({ title, email }) => (
              <Text
                key={title + email}
                as="a"
                size="smallBody"
                href={`mailto:${email}`}
                text={title}
                style={{ display: 'block' }}
              />
            ))}
          </div>
        </S.OfficeInfoBox>
      </S.OfficeInfoWrapper>
      {currentOffice.image && (
        <S.OfficeImage
          src={currentOffice.image.url}
          alt={currentOffice.image.alternativeText}
        />
      )}
    </>
  );

  return (
    <S.OfficeSectionGrid ref={ref}>
      <S.OfficesSectionHeadline>
        <RichText
          as="h2"
          size="heading2"
          richText={sectionTitle}
          defaultFormatting
        />
      </S.OfficesSectionHeadline>

      <S.OfficesList>
        <S.SliderInner
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {sliderItemsComp}
        </S.SliderInner>
      </S.OfficesList>

      {officeCard}
    </S.OfficeSectionGrid>
  );
};

export default OfficeSection;
